import React, { useState } from "react";
import "../../summaruData/summaruTable/SummaruTable.css";
import { Tooltip } from "antd";

interface AnimalTableProps {
  data: any;
  isLoading: boolean;
}

const isAnimalDataApiResponse = (data: any): boolean => {
  return data && data.content && Array.isArray(data.content);
};

export const AnimalTable: React.FC<AnimalTableProps> = ({ data, isLoading }) => {
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  const toggleRowExpansion = (rowId: number) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(rowId)) {
      newExpandedRows.delete(rowId);
    } else {
      newExpandedRows.add(rowId);
    }
    setExpandedRows(newExpandedRows);
  };

  const formatNumber = (num: number | null | undefined): string => {
    if (num === null || num === undefined) return '-';
    return new Intl.NumberFormat('ru-RU').format(num);
  };

  const renderProgressCircles = (
    completed: number,
    notCompleted: number,
    notRequired: number
  ) => {
    return (
      <div className="progress-circles">
        <div className="circle-wrapper" title={`Выполнено: ${completed}`}>
          <div className="progress-circle1 completed">{completed}</div>
        </div>
        <div className="circle-wrapper" title={`Не выполнено: ${notCompleted}`}>
          <div className="progress-circle1 not-completed">{notCompleted}</div>
        </div>
        <div className="circle-wrapper" title={`Не требуется: ${notRequired}`}>
          <div className="progress-circle1 not-required">{notRequired}</div>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (!isAnimalDataApiResponse(data)) {
    return (
      <div className="no-data-container">
        Нет данных для отображения
      </div>
    );
  }

  const content = data.content || [];
  const totalElements = data.totalElements || 0;

  if (!content || content.length === 0) {
    return (
      <div className="no-data-container">
        Нет данных для отображения
      </div>
    );
  }

  const pageSize = data.size || 15;
  const currentPage = data.page || 1;
  const startIndex = (currentPage - 1) * pageSize + 1;

  return (
    <div className="summary-table-containersummaru">
      <table className="summary-table">
        <thead>
        <tr style={{ background: "#e0e0e0", borderRadius: "10px" }}>
            <th className="row-number">№</th>
            <th>Фермер</th>
            <th>Область</th>
            <th>Район</th>
            <th>Статусы задач</th>
            <th>Адрес</th>
            <th>Прогресс</th>
            <th>Вид участка</th>
            <th>Площадь (га)</th>
          </tr>
        </thead>
        <tbody>
          {content.map((item: any, index: number) => {
            const isExpanded = expandedRows.has(item.id);

            return (
              <React.Fragment key={item.id}>
                <tr>
                  <td className="row-number">{startIndex + index}</td>
                  <td>
                    <div className="farmer-cell">
                      <button
                        className={`expand-button ${isExpanded ? 'expanded' : ''}`}
                        onClick={() => toggleRowExpansion(item.id)}
                      >
                        <svg viewBox="0 0 16 16" fill="currentColor">
                          <path d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z"/>
                        </svg>
                      </button>
                      {item.farmerFullname}
                    </div>
                  </td>
                  <td>{item.regionalUnit.nameRu}</td>
                  <td>{item.areaUnit.nameRu}</td>
                  <td>
                      <Tooltip title={`Выполнено: ${item.taskCompletedCount}`}>
                        <span className='status-badge green'>{item.taskCompletedCount}</span>
                      </Tooltip>

                      <Tooltip title={`Не требуется: ${item.taskNotRequiredCount}`}>
                        <span className='status-badge yellow'>{item.taskNotRequiredCount}</span>
                      </Tooltip>

                      <Tooltip title={`Не выполнено: ${item.taskNotCompetedCount}`}>
                        <span className='status-badge red'>{item.taskNotCompetedCount}</span>
                      </Tooltip>
                    </td>
                  <td>{item.address}</td>
                  <td>
                    <span className="progress-badge">
                      {item.taskInProgressCount}%
                    </span>
                  </td>
                  <td>{item.landPlotType?.nameRu || '-'}</td>
                  <td>
                    <span className="area-value">{formatNumber(item.landArea)} ГА</span>
                  </td>
                </tr>
                {isExpanded && item.tasks && item.tasks.length > 0 && (
                  <tr className="expanded-content">
                    <td colSpan={9}>
                      <div className="tasks-container">
                        <table className="tasks-table">
                          <thead>
                            <tr>
                              <th>ФИО Сотрудника</th>
                              <th>Тип задачи</th>
                              <th>Дата проведения</th>
                              <th>Описание работы</th>
                              <th>Статус задачи</th>
                              {/* <th>Результат работы</th> */}
                            </tr>
                          </thead>
                          <tbody>
                            {item.tasks.map((task: any) => (
                              <tr key={task.id}>
                                <td>
                                  <span className="task-employee">{task.ruarFullname || '-'}</span>
                                </td>
                                <td>{task.workType?.nameRu || '-'}</td>
                                <td>
                                  <span className="task-date">{task.dateOfWork || '-'}</span>
                                </td>
                                <td>
                                  <span className="task-description">
                                    {task.description || '-'}
                                  </span>
                                </td>
                                <td>
                                  <span className={`task-status ${task.workStatus?.id === 3 ? 'completed' : task.workStatus?.id === 2 ? 'in-progress' : 'not-required'}`}>
                                    {task.workStatus?.nameRu || '-'}
                                  </span>
                                </td>
                                {/* <td>
                                  <span className="task-result">
                                    {task.workStatus?.id === 3 ? 'Выполнено' :
                                     task.workStatus?.id === 2 ? 'В работе' :
                                     task.workStatus?.id === 1 ? 'Не требуется' : '-'}
                                  </span>
                                </td> */}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>

      <div className="pagination-container">
        <div className="pagination-info">
          Показано {startIndex}-{Math.min(startIndex + pageSize - 1, totalElements)} из {totalElements} записей
        </div>
      </div>
    </div>
  );
};